import React from 'react';
import { useNavigate } from 'react-router-dom';

const p = 'text-[14px] text-[#8c8c8c] leading-[1.6]';
const h2 = 'text-[18px] font-medium text-black mb-3 mt-8 first:mt-0';
const block = 'space-y-3';
const list = 'list-disc pl-6 space-y-2 text-[14px] text-[#8c8c8c] leading-[1.6]';

const CONTACT_EMAIL = 'help@bookforpaws.com';
const REGISTERED_ADDRESS = 'WEWORK, NESCO, Building 4, North Wing, NESCO IT Park, Western Express Hwy, NESCO, Goregaon East, Mumbai, Maharashtra 400063';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <div className="bg-[#FAF7F2] md:px-[120px] p-[20px] md:pt-[60px] md:pb-[20px] gap-[16px] flex items-center">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="hover:bg-gray-100 rounded-full"
          aria-label="Go back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <g clipPath="url(#clip0_privacy_back)">
              <path d="M5 12H19" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 12L11 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 12L11 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <defs>
              <clipPath id="clip0_privacy_back">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
        <h1 className="text-[20px] font-medium text-black tracking-[-1px]">Privacy Policy</h1>
      </div>

      <div className="md:px-[120px] p-[20px] md:py-[20px] flex flex-col gap-[8px] pb-10">
        <div className="bg-white rounded-[16px] p-[24px] shadow-sm">
          <p className="text-[12px] uppercase tracking-wide text-[#8c8c8c] mb-1">BookForPaws</p>
          <p className="text-[14px] text-[#8c8c8c] mb-6">Last updated: 7 April 2026</p>

          <div className="space-y-3">
            <p className={p}>
              BookForPaws values the trust that users place in us. Protecting your privacy and ensuring the security of
              your personal information is important to us. This Privacy Policy explains how BookForPaws collects, uses,
              stores, processes and protects your information when you access or use the website{' '}
              <a
                href="https://www.bookforpaws.com"
                className="text-[#FF5E4C] underline underline-offset-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.bookforpaws.com
              </a>
              , the mobile application and any related services operated by Joey &amp; Chandler Tech Ventures LLP under
              the brand &quot;BookForPaws&quot; (collectively referred to as the &quot;Platform&quot;, &quot;BFP&quot;,
              &quot;we&quot;, &quot;us&quot; or &quot;our&quot;).
            </p>
            <p className={p}>
              By accessing or using the Platform, you agree to the collection, storage and use of your information in
              accordance with this Privacy Policy.
            </p>
          </div>

          <div className="mt-6 space-y-8">
            <section>
              <h2 className={h2}>Electronic Record</h2>
              <div className={block}>
                <p className={p}>
                  This Privacy Policy is an electronic record in the form of an electronic contract formed under the
                  provisions of the Information Technology Act, 2000 and the rules made thereunder including the
                  Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or
                  Information) Rules, 2011, as amended from time to time.
                </p>
                <p className={p}>
                  This Privacy Policy does not require any physical, electronic or digital signature.
                </p>
                <p className={p}>
                  By using the Platform, you acknowledge and accept the terms of this Privacy Policy and consent to the
                  practices described herein.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Relationship with Terms of Use</h2>
              <div className={block}>
                <p className={p}>
                  This Privacy Policy forms an integral part of the Terms and Conditions governing the use of the
                  Platform and shall be read together with such Terms.
                </p>
                <p className={p}>
                  Any capitalised terms not defined in this Privacy Policy shall have the meanings assigned to them in
                  the Terms and Conditions.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>About Us</h2>
              <div className={block}>
                <p className={p}>
                  The Platform is owned and operated by Joey &amp; Chandler Tech Ventures LLP, having its registered
                  office at {REGISTERED_ADDRESS}. In this Privacy Policy, references to &quot;User&quot;, &quot;you&quot;
                  or &quot;your&quot; refer to any person who accesses, browses, registers, makes bookings or
                  otherwise interacts with the Platform.
                </p>
                <p className={p}>If you have any questions or concerns regarding this Privacy Policy, you may contact us at:</p>
                <div className="p-4 bg-[#FF5E4C] bg-opacity-10 border border-[#FF5E4C] border-opacity-20 rounded-lg">
                  <p className={p}>
                    <strong className="text-black">Email:</strong>{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF5E4C] underline underline-offset-2">
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                  <p className={`${p} mt-2`}>
                    <strong className="text-black">Address:</strong> {REGISTERED_ADDRESS}
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className={h2}>Information We Collect</h2>
              <div className={block}>
                <p className={p}>
                  We may collect personal and non-personal information from users in order to provide and improve the
                  services available on the Platform.
                </p>
                <p className={p}>The information collected may include:</p>
                <ul className={list}>
                  <li>
                    name, email address, phone number, postal address, billing address, login credentials, profile
                    information, booking details, pet-related information where voluntarily provided by users, payment
                    information, payment card details or banking details processed through authorised payment gateways,
                    IP address, device information, browser information, location data, usage data relating to
                    interactions with the Platform, and any other information voluntarily provided by users while using
                    the Platform.
                  </li>
                </ul>
                <p className={p}>
                  Where users make bookings for pet-friendly accommodations, events, workshops, training programs or
                  other services, additional information relevant to the booking may be collected.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Use of Information</h2>
              <div className={block}>
                <p className={p}>
                  The information collected from users may be used for various purposes including:
                </p>
                <ul className={list}>
                  <li>facilitating bookings of pet-friendly accommodations, events or services;</li>
                  <li>processing payments and confirming bookings;</li>
                  <li>communicating booking confirmations, reminders and updates;</li>
                  <li>providing customer support and responding to queries;</li>
                  <li>improving the functionality, security and user experience of the Platform;</li>
                  <li>personalising user experience and recommendations;</li>
                  <li>preventing fraudulent or unlawful activities;</li>
                  <li>
                    sending notifications, promotional communications or marketing materials where permitted by law;
                  </li>
                  <li>complying with legal obligations and regulatory requirements.</li>
                </ul>
                <p className={p}>
                  The Platform may also use aggregated or anonymised data for analytics, research and business
                  development purposes.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Storage and Retention of Information</h2>
              <div className={block}>
                <p className={p}>
                  User information may be stored electronically on servers located in India or other jurisdictions
                  permitted under applicable laws. The Platform may retain user information for as long as necessary to
                  fulfil the purposes described in this Privacy Policy, including compliance with legal, regulatory, tax
                  or accounting requirements. Information may also be retained for dispute resolution, enforcement of
                  agreements and fraud prevention.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Sharing of Information</h2>
              <div className={block}>
                <p className={p}>
                  The Platform may share user information with third parties where necessary for providing services
                  through the Platform. Such third parties may include vendors providing pet-friendly accommodations or
                  events, payment gateway providers, banks and financial institutions, service providers, marketing
                  partners, logistics providers, technology service providers, analytics providers, and legal or
                  regulatory authorities where required under applicable law.
                </p>
                <p className={p}>
                  Information may also be shared with affiliates, employees, contractors and authorised agents of the
                  Platform strictly on a need-to-know basis. The Platform does not sell or rent personal information to
                  third parties for their independent marketing purposes without user consent. Notwithstanding the above,
                  the Platform reserves the right to disclose user information where required by law, court order,
                  government authority or regulatory body.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Vendor Access to Information</h2>
              <div className={block}>
                <p className={p}>
                  Where a user makes a booking with a vendor through the Platform, certain information necessary for
                  fulfilling the booking may be shared with the relevant vendor. Such information may include the
                  user&apos;s name, contact details, booking details and any relevant pet-related information provided by
                  the user. The vendor shall use such information solely for the purpose of providing the booked
                  service.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>User Representations</h2>
              <div className={block}>
                <p className={p}>
                  By providing information to the Platform, the user represents and warrants that all information
                  provided is true, accurate and complete. Users further represent that they are authorised to provide
                  any information relating to third parties including pets, co-travellers or participants where
                  applicable. The user agrees to indemnify and hold harmless the Platform from any loss or liability
                  arising from inaccurate or unauthorised information provided by the user.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Cookies and Tracking Technologies</h2>
              <div className={block}>
                <p className={p}>
                  The Platform may use cookies, web beacons and similar tracking technologies to enhance user experience
                  and analyse usage patterns. Cookies are small data files stored on a user&apos;s device that help the
                  Platform recognise returning users, remember preferences and improve functionality. Users may choose
                  to disable cookies through their browser settings; however, doing so may affect certain features of the
                  Platform.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Device and Location Information</h2>
              <div className={block}>
                <p className={p}>
                  When users access the Platform through a mobile device or application, the Platform may collect
                  device-specific information including device identifiers, operating system, device model, mobile
                  network information and approximate location. Such information may be used to improve platform
                  performance, provide location-based services and enhance user experience.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Data Security</h2>
              <div className={block}>
                <p className={p}>
                  The Platform implements reasonable security practices and procedures in accordance with applicable laws
                  to protect user information from unauthorised access, misuse, disclosure, alteration or destruction.
                  These measures may include technical safeguards, administrative controls and secure server
                  infrastructure. However, no method of transmission over the internet or electronic storage is
                  completely secure and the Platform cannot guarantee absolute security of information.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Third Party Links</h2>
              <div className={block}>
                <p className={p}>
                  The Platform may contain links to third-party websites, vendor pages or external services. The
                  Platform does not control such websites and is not responsible for the privacy practices or content of
                  such third parties. Users are encouraged to review the privacy policies of such external websites
                  before providing any personal information.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>User Account Responsibility</h2>
              <div className={block}>
                <p className={p}>
                  Users who create accounts on the Platform are responsible for maintaining the confidentiality of their
                  login credentials including username and password. The Platform shall not be liable for any loss or
                  misuse arising from unauthorised access to a user&apos;s account due to negligence or failure to
                  safeguard login credentials.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>User Rights</h2>
              <div className={block}>
                <p className={p}>
                  Users may request access to, correction of or deletion of their personal information by contacting the
                  Platform through the contact details provided above, subject to applicable legal limitations. The
                  Platform reserves the right to retain certain information where required by law or for legitimate
                  business purposes.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Children&apos;s Privacy</h2>
              <div className={block}>
                <p className={p}>
                  The Platform is not intended for use by individuals under the age of 18 without supervision of a parent
                  or legal guardian. The Platform does not knowingly collect personal information from minors without
                  appropriate consent.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Changes to this Privacy Policy</h2>
              <div className={block}>
                <p className={p}>
                  The Platform reserves the right to update or modify this Privacy Policy at any time. Any changes shall
                  be effective upon publication of the revised Privacy Policy on the Platform. Users are encouraged to
                  periodically review this Privacy Policy to stay informed about how their information is protected.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Contact</h2>
              <div className={`${block} mt-3`}>
                <p className={p}>
                  If you have any questions, concerns or requests relating to this Privacy Policy, you may contact:
                </p>
                <div className="p-4 bg-[#FF5E4C] bg-opacity-10 border border-[#FF5E4C] border-opacity-20 rounded-lg">
                  <p className={`${p} font-medium text-black`}>BookForPaws</p>
                  <p className={`${p} mt-1`}>Joey &amp; Chandler Tech Ventures LLP</p>
                  <p className={`${p} mt-3`}>
                    <strong className="text-black">Email:</strong>{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF5E4C] underline underline-offset-2">
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                  <p className={`${p} mt-2`}>
                    <strong className="text-black">Address:</strong> {REGISTERED_ADDRESS}
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
